import { Loader, Stack } from "@mantine/core";
import { CarIcon, GearIcon, RulerIcon, TruckIcon } from "@phosphor-icons/react";
import { ComponentProps, useEffect } from "react";
import { Control, FieldValues, Path, PathValue, useFormContext, useWatch } from "react-hook-form";
import { z } from "zod";

import { getImplementByType } from "@/features/dashboard/caminhoes/utils/getImplementByType";
import { useTruckData } from "@/hooks";
import { GLOBAL_SCHEMA, REGEX } from "@/lib/zod";

import { Input } from "../_Input";

export type TruckSchema = z.infer<typeof truckSchema>;
const truckSchema = z.object({
  ...GLOBAL_SCHEMA.TRUCK.omit({ renavam: true }).shape,
  renavam: z.string().min(1, "Renavam é obrigatório").regex(REGEX.RENAVAM, "Renavam inválido")
});

// Ajuste final: OmitUnnecessary não precisa ser genérico, apenas Omit<ComponentProps<any>, ...>

type OmitUnnecessary = "name" | "control";

type TruckFieldsImplProps<TFieldValues extends FieldValues, TName extends Path<TFieldValues> = Path<TFieldValues>> = {
  control: Control<TFieldValues>;
  name: TName;
  children?: React.ReactNode;
  slot?: {
    wrapper?: {
      props: ComponentProps<typeof Stack>;
    };
    plate?: {
      props: Omit<ComponentProps<typeof Input.Text<TFieldValues>>, OmitUnnecessary>;
    };
    renavam?: {
      props: Omit<ComponentProps<typeof Input.Text<TFieldValues>>, OmitUnnecessary>;
    };
    brand?: {
      props: Omit<ComponentProps<typeof Input.Select<TFieldValues>>, OmitUnnecessary>;
    };
    model?: {
      props: Omit<ComponentProps<typeof Input.Select<TFieldValues>>, OmitUnnecessary>;
    };
    color?: {
      props: Omit<ComponentProps<typeof Input.Color<TFieldValues>>, OmitUnnecessary>;
    };
    implementType?: {
      props: Omit<ComponentProps<typeof Input.Select<TFieldValues>>, OmitUnnecessary>;
    };
    shaftQuantity?: {
      props: Omit<ComponentProps<typeof Input.Number<TFieldValues>>, OmitUnnecessary>;
    };
    grossCapacity?: {
      props: Omit<ComponentProps<typeof Input.Number<TFieldValues>>, OmitUnnecessary>;
    };
    netCapacity?: {
      props: Omit<ComponentProps<typeof Input.Number<TFieldValues>>, OmitUnnecessary>;
    };
  };
};

function TruckFieldsImpl<TFieldValues extends FieldValues, TName extends Path<TFieldValues> = Path<TFieldValues>>({
  control,
  name,
  children,
  slot
}: TruckFieldsImplProps<TFieldValues, TName>) {
  const { setValue } = useFormContext<TFieldValues>();

  const brand = useWatch<TFieldValues, Path<TFieldValues>>({
    control,
    name: `${name}.brand` as Path<TFieldValues>
  });
  const implementType = useWatch<TFieldValues, Path<TFieldValues>>({
    control,
    name: `${name}.implementType` as Path<TFieldValues>
  });
  const { grossCapacity, netCapacity, shaftQuantity } = getImplementByType(implementType);

  const { data, state } = useTruckData({ brandId: Number(brand) });

  const brandDisabled = data.brands.isLoading || data.brands.isError;
  const modelDisabled = !brand || data.modelByBrand.isLoading || data.modelByBrand.isError;

  useEffect(() => {
    if (implementType) {
      setValue(`${name}.shaftQuantity` as Path<TFieldValues>, shaftQuantity as PathValue<TFieldValues, Path<TFieldValues>>);
      setValue(`${name}.grossCapacity` as Path<TFieldValues>, grossCapacity as PathValue<TFieldValues, Path<TFieldValues>>);
      setValue(`${name}.netCapacity` as Path<TFieldValues>, netCapacity as PathValue<TFieldValues, Path<TFieldValues>>);
    }
  }, [implementType]);

  return (
    <Stack>
      {children}

      <div className="grid grid-cols-1 gap-[inherit] md:grid-cols-2">
        <Input.Text
          control={control}
          leftSection={<CarIcon weight="bold" />}
          maskProps={{ mask: "*******" }}
          placeholder="Placa (Apenas letras e números)"
          name={`${name}.plate` as Path<TFieldValues>}
          label="Placa"
          {...slot?.plate?.props}
        />

        <Input.Text
          control={control}
          leftSection={<TruckIcon weight="bold" />}
          maskProps={{ mask: "000000000-00" }}
          placeholder="Renavam"
          name={`${name}.renavam` as Path<TFieldValues>}
          label="Renavam"
          {...slot?.renavam?.props}
        />
      </div>

      <div className="grid grid-cols-1 gap-[inherit] md:grid-cols-3">
        <Input.Select
          control={control}
          data={data.brands.data.options}
          placeholder="Marca"
          name={`${name}.brand` as Path<TFieldValues>}
          leftSection={data.brands.isFetching && <Loader size="sm" />}
          disabled={brandDisabled}
          label="Marca"
          {...slot?.brand?.props}
        />

        <Input.Select
          control={control}
          data={data.modelByBrand.data.options}
          placeholder="Modelo"
          name={`${name}.model` as Path<TFieldValues>}
          leftSection={data.modelByBrand.isFetching && <Loader size="sm" />}
          disabled={modelDisabled}
          label="Modelo"
          {...slot?.model?.props}
        />

        <Input.Color name={`${name}.color` as Path<TFieldValues>} placeholder="Cor" control={control} {...slot?.color?.props} label="Cor" />
      </div>

      <Input.Select
        control={control}
        data={state.implements}
        placeholder="Tipo de implemento"
        name={`${name}.implementType` as Path<TFieldValues>}
        {...slot?.implementType?.props}
        label="Tipo de implemento"
      />

      <div className="grid grid-cols-1 gap-[inherit] md:grid-cols-3">
        <Input.Number
          control={control}
          leftSection={<GearIcon weight="bold" />}
          placeholder="Quantidade de eixos"
          name={`${name}.shaftQuantity` as Path<TFieldValues>}
          defaultValue={0 as PathValue<TFieldValues, Path<TFieldValues>>}
          label="Eixos"
          {...slot?.shaftQuantity?.props}
        />

        <Input.Number
          control={control}
          leftSection={<RulerIcon weight="bold" />}
          placeholder="Capacidade bruta em tonelada"
          name={`${name}.grossCapacity` as Path<TFieldValues>}
          defaultValue={0 as PathValue<TFieldValues, Path<TFieldValues>>}
          label="Capacidade bruta"
          {...slot?.grossCapacity?.props}
        />

        <Input.Number
          control={control}
          leftSection={<RulerIcon weight="bold" />}
          placeholder="Capacidade líquida em tonelada"
          name={`${name}.netCapacity` as Path<TFieldValues>}
          defaultValue={0 as PathValue<TFieldValues, Path<TFieldValues>>}
          label="Capacidade líquida"
          {...slot?.netCapacity?.props}
        />
      </div>
    </Stack>
  );
}

export const TruckFields = Object.assign(TruckFieldsImpl, {
  $schema: truckSchema
});
